/**
 * Traduce los errores del sistema a algo que una persona entienda.
 *
 * La base lanza códigos —`APPOINTMENT_OVERLAP:cyKaHdLgfOg0VM3ZU`,
 * `PET_CUSTOMER_MISMATCH`, `INSUFFICIENT_STOCK`— que son útiles para
 * diagnosticar y no significan nada para quien está atendiendo el mostrador.
 * La dueña de La Perruquería lo reportó el 25/09/2026: «no es un error que el
 * usuario debería ver porque no entiende nada».
 *
 * Tres reglas al escribir un mensaje aquí:
 *
 * 1. **Decir qué pasó**, no cómo se llama el fallo.
 * 2. **Decir qué hacer**, cuando hay algo que hacer. «Ese horario ya está
 *    ocupado» deja al usuario igual de atascado que el código; «elige otra
 *    hora» lo saca.
 * 3. **No culpar a quien lo lee.** Casi siempre es el sistema el que no supo
 *    explicarse antes.
 *
 * Los códigos que no estén aquí caen al mensaje genérico. Se prefiere eso a
 * mostrar el crudo: un texto en mayúsculas con dos puntos y un identificador
 * parece que el sistema se rompió, aunque sea una validación funcionando.
 */

const MENSAJES: Record<string, string> = {
  // ── Agenda ──────────────────────────────────────────────────────────
  APPOINTMENT_OVERLAP:
    'Ya hay una cita a esa hora. Elige otro horario o revisa la agenda del día.',
  APPOINTMENT_NOT_OWNED: 'Esa cita no es de tu salón.',
  PET_CUSTOMER_MISMATCH:
    'Esa mascota no está registrada a nombre de ese cliente. Revisa a quién pertenece.',
  // Lo levantan dos caminos —agendar desde el salón y reservar desde el
  // portal—, así que el texto tiene que servirle a los dos lectores.
  PET_NOT_OWNED:
    'Esa mascota no aparece registrada a ese nombre. Recarga la página e inténtalo otra vez.',
  INVALID_SERVICES: 'Elige al menos un servicio para la cita.',
  TAMANO_INVALIDO: 'El tamaño de la mascota no es válido. Elige pequeño, mediano, grande o grande pro.',
  INVALID_DATE: 'Esa fecha no es válida.',
  CITA_NO_EXISTE: 'Esa cita ya no existe. Puede que alguien la haya eliminado.',
  CITA_YA_COBRADA: 'Esa cita ya se cobró en caja.',
  ESTADIA_YA_COBRADA:
    'Esa estadía ya se cobró: no se puede cambiar el precio. Anula la venta si hay que corregirla.',
  INVALID_TRANSITION: 'No se puede pasar la cita a ese estado desde el actual.',

  // ── Caja y ventas ───────────────────────────────────────────────────
  EMPTY_CART: 'El carrito está vacío. Agrega al menos un producto o servicio.',
  INSUFFICIENT_STOCK:
    'No hay suficiente inventario de ese producto. Revisa las existencias antes de venderlo.',
  ZERO_QUANTITY: 'La cantidad tiene que ser mayor que cero.',
  INVALID_QUANTITY: 'Esa cantidad no es válida.',
  INVALID_AMOUNT: 'Ese monto no es válido.',
  MONTO_CERO: 'El monto tiene que ser mayor que cero.',
  INVALID_PAYMENT_AMOUNT: 'El pago no cuadra con el total de la venta.',
  PAYMENT_MISMATCH: 'Lo pagado no coincide con el total. Revisa los montos.',
  ORDER_NOT_FOUND: 'Esa venta ya no existe.',
  ORDER_NOT_OPEN: 'Esa venta ya se cerró: no se puede modificar.',
  ALREADY_VOID: 'Esa venta ya estaba anulada.',
  ALREADY_OPEN: 'Ya hay una caja abierta.',
  // Lo lanza todo cobro sin caja abierta en la sede (desde el 12/10/2026).
  REGISTER_NOT_OPEN: 'La caja está cerrada. Ábrela en POS → Cierre de caja y vuelve a cobrar.',
  REGISTER_NOT_FOUND: 'No se encontró esa caja.',
  FINALIZED: 'Eso ya está cerrado y no se puede cambiar.',
  PRODUCT_NOT_FOUND: 'Ese producto ya no existe.',
  EMPTY_PURCHASE: 'La compra no tiene productos.',
  REASON_REQUIRED: 'Escribe el motivo.',
  INVALID_REASON: 'Ese motivo no es válido.',
  NOTES_REQUIRED: 'Escribe una nota explicando el cambio.',

  // ── Clientes y mascotas ─────────────────────────────────────────────
  CUSTOMER_NOT_FOUND: 'Ese cliente ya no existe.',
  CLIENTE_NO_EXISTE: 'Ese cliente ya no existe.',
  PHONE_INVALID: 'Ese número de teléfono no es válido.',
  INVALID_EMAIL: 'Ese correo no es válido.',
  NOMBRE_REQUERIDO: 'Escribe un nombre.',
  NOMBRE_VACIO: 'Escribe un nombre.',

  // ── Hotel y guardería ───────────────────────────────────────────────
  // La cuenta de la habitación (12/10/2026).
  SALIDA_NO_POSTERIOR: 'La nueva salida tiene que ser después de la que ya tiene la estadía.',
  ESTADIA_NO_ACTIVA:
    'Esa estadía ya terminó o se canceló: no se puede alargar. Si el perro sigue, crea una reserva nueva.',
  CARGO_YA_COBRADO:
    'Eso ya se cobró en caja. Para corregirlo, anula la venta: vuelve a quedar en la cuenta.',
  SIN_MASCOTAS: 'Marca al menos una mascota para la reserva.',
  SUITE_NO_EXISTE: 'Esa habitación ya no existe. Recarga la página y elige otra.',
  PET_NOT_FOUND: 'Esa mascota ya no existe. Recarga la página.',
  ABONO_MAYOR_QUE_EL_SALDO:
    'El abono no puede ser mayor que lo que falta por pagar. Si el precio subió, ajústalo primero.',

  // ── Reservar desde el portal con abono ────────────────────────────────
  // El texto es para el dueño de la mascota, no para el salón: es quien ve
  // estos errores, y no sabe qué es un RPC ni un bucket.
  ABONO_INSUFICIENTE:
    'El abono no llega al mínimo que pide el salón. Revisa el monto que escribiste.',
  FALTA_EL_COMPROBANTE:
    'Falta la captura del pago. Adjúntala para que el salón pueda confirmar tu reserva.',
  COMPROBANTE_DE_OTRO_SALON:
    'No pudimos guardar tu comprobante. Vuelve a adjuntarlo, por favor.',
  CUENTA_NO_ENCONTRADA:
    'Esa cuenta de pago ya no está disponible. Recarga la página y elige otra.',
  SIN_TARIFA:
    'No hay tarifa configurada para ese tamaño de mascota. Ponla en Hotel → Tarifas.',
  INVALID_DAYS: 'La cantidad de días no es válida.',
  DURACION_INVALIDA: 'Esa duración no es válida.',

  // ── Membresías ──────────────────────────────────────────────────────
  ALREADY_MEMBER: 'Ese cliente ya tiene esta membresía activa.',
  NOT_MEMBER: 'Ese cliente no tiene esta membresía.',

  // ── Servicios y precios ─────────────────────────────────────────────
  PRECIO_NEGATIVO: 'El precio no puede ser negativo.',
  ADICIONAL_NO_EXISTE: 'Ese adicional ya no existe.',

  // ── Suscripción y pagos de la plataforma ────────────────────────────
  GATEWAY_DISABLED:
    'El pago en línea no está disponible ahora mismo. Puedes transferir y subir el comprobante.',
  PLAN_SIN_PRECIO: 'Ese plan no tiene precio configurado. Escríbenos y lo revisamos.',
  PLAN_NO_EXISTE: 'Ese plan ya no está disponible.',
  INVALID_PLAN: 'Ese plan no es válido.',
  INVALID_MONTHS: 'La cantidad de meses no es válida.',
  PERIODOS_INVALIDOS: 'La cantidad de periodos no es válida.',
  INTENT_NOT_FOUND: 'No se encontró ese intento de pago.',
  NOT_PENDING: 'Ese pago ya fue revisado.',
  NO_PAYMENTS: 'No hay pagos registrados.',
  NO_HAY_SUSCRIPCION_QUE_CANCELAR:
    'Esa empresa no tiene ninguna suscripción vigente ni pendiente que cancelar.',
  // VetCare (14/10/2026). Los lee el veterinario en plena consulta: corto y
  // con lo que tiene que hacer.
  CONSULTA_FIRMADA:
    'Esta consulta ya está firmada y no se puede cambiar. Si hay algo que corregir, agrega una nota de corrección.',
  CONSULTA_NO_EXISTE: 'Esa consulta ya no existe.',
  CONSULTA_NO_FIRMADA: 'Las notas de corrección se agregan a consultas ya firmadas.',
  CONSULTA_CON_APLICACIONES:
    'No se puede descartar: ya tiene vacunas registradas. Fírmala o agrega lo que falte.',
  SIN_TARJETA_PROFESIONAL:
    'Para firmar necesitas tu tarjeta profesional. Agrégala en Mi cuenta y vuelve a firmar.',
  FALTA_DIAGNOSTICO: 'Escribe un diagnóstico (presuntivo o confirmado) antes de firmar.',
  LOTE_VENCIDO: 'Ese lote está vencido para la fecha de aplicación. Revisa el vencimiento del frasco.',
  FORMULA_INVALIDA: 'La fórmula no es válida. Revisa los medicamentos.',
  TIPO_DE_APLICACION_INVALIDO: 'Elige si es vacuna o desparasitación.',
  TARJETA_INVALIDA: 'La tarjeta profesional es demasiado larga.',
  TEXTO_VACIO: 'Escribe algo antes de guardar.',
  REGISTRO_CLINICO_INMUTABLE: 'Los registros clínicos no se editan ni se borran.',
  ADJUNTO_DE_OTRA_CLINICA: 'Ese archivo no es de esta clínica.',
  ADJUNTO_NO_EXISTE: 'Ese archivo ya no existe.',
  // Cobro por módulos (14/10/2026).
  PLAN_NO_DISPONIBLE: 'Ese plan ya no se ofrece. Elige uno de los que aparecen en la lista.',
  FALTA_LA_CUENTA: 'Elige a qué cuenta transferiste.',
  CANTIDAD_INVALIDA: 'La cantidad no puede ser negativa.',
  PRECIO_INVALIDO: 'El precio tiene que ser mayor que cero, o vacío para quitarlo.',

  // ── Permisos y sesión ───────────────────────────────────────────────
  FORBIDDEN: 'No tienes permiso para hacer eso. Pídeselo a quien administra el salón.',
  NO_TENANT: 'Tu sesión perdió la empresa. Vuelve a entrar.',
  NOT_PORTAL_ACCOUNT: 'Esa cuenta no es del portal de clientes.',
  INVALID_OR_EXPIRED_INVITE:
    'Esa invitación ya no sirve: caducó o alguien la usó. Pide una nueva.',
  ROLE_NOT_FOUND: 'Ese rol ya no existe.',
  ROL_NO_EXISTE: 'Ese rol ya no existe.',
  USUARIO_NO_EXISTE: 'Ese usuario ya no existe.',

  // ── Administración de la plataforma ─────────────────────────────────
  EMPRESA_NO_EXISTE: 'Esa empresa ya no existe.',
  SLUG_EN_USO: 'Ya hay otra empresa con esa dirección. Elige otra.',
  SLUG_INVALIDO: 'Esa dirección no es válida: usa solo letras, números y guiones.',
  MODULO_NO_EXISTE: 'Ese módulo no existe.',
  CANNOT_DELETE_DEFAULT_BRANCH: 'No se puede eliminar la sede principal.',
  NO_BRANCH: 'No hay ninguna sede configurada.',
  CODIGO_VACIO: 'Escribe un código.',
  NOT_FOUND: 'Eso ya no existe.',
  INVALID_STATUS: 'Ese estado no es válido.',

  // ── Facturación electrónica ─────────────────────────────────────────
  // Los lee el salón y el superadministrador. Ninguno detiene un cobro: la
  // venta ya está hecha cuando la factura falla, y el mensaje tiene que
  // dejarlo claro para que nadie la vuelva a cobrar.
  FE_APAGADA:
    'La facturación electrónica no está encendida para este salón. La venta quedó cobrada con su recibo.',
  SIN_TOKEN:
    'Falta conectar la cuenta de facturación electrónica. Netcloud tiene que cargarla antes de emitir.',
  TOKEN_VENCIDO:
    'La conexión con el proveedor de facturación venció. Avísale a Netcloud para renovarla; la venta quedó cobrada.',
  TOKEN_INVALIDO: 'Eso no parece un token de MATIAS. Cópialo completo, sin espacios.',
  TOKEN_DE_OTRA_EMPRESA:
    'Ese token es de otra empresa de MATIAS, no de la que tenía este salón. Revísalo antes de reemplazarlo.',
  TOKEN_YA_USADO_EN_OTRA_EMPRESA:
    'Ese token ya está cargado en otro salón. Con él, las facturas saldrían con el NIT equivocado.',
  SIN_RESOLUCION:
    'No hay una resolución de facturación activa. Netcloud tiene que cargarla.',
  SIN_RESOLUCION_NC:
    'No hay resolución para notas crédito. Sin ella no se puede corregir ni anular una factura.',
  RESOLUCION_VENCIDA:
    'La resolución de facturación de la DIAN está vencida. Hay que cargar la nueva.',
  RESOLUCION_AGOTADA:
    'Se acabaron los números autorizados por la DIAN. Hay que pedir una resolución nueva.',
  // El documento ya tiene número de una resolución que venció mientras
  // esperaba (MATIAS caído, un rechazo sin atender). Reintentarlo no sirve
  // nunca —la DIAN rechaza un número de una resolución vencida— y la única
  // salida es que Netcloud lo renumere con la vigente. Sin decirlo, la venta
  // se quedaba en «Por emitir» para siempre con un error genérico.
  RESOLUCION_DEL_DOCUMENTO_VENCIDA:
    'Este documento se numeró con una resolución de la DIAN que ya venció, así que reintentarlo no sirve. Netcloud tiene que renumerarlo con la resolución vigente («Renumerar» en Facturación). La venta sigue cobrada.',
  // En habilitación nada sale solo: la DIAN pide UNA factura de prueba y se
  // emite a mano, eligiendo la venta.
  FE_HABILITACION_MANUAL:
    'En habilitación las facturas no salen solas al cobrar. La factura de prueba se emite a mano: en Facturación → «Por emitir», elige una venta y pulsa «Emitir factura de prueba».',
  RESOLUCION_NO_EXISTE: 'Esa resolución ya no existe.',
  ORDEN_NO_PAGADA: 'Solo se factura una venta ya cobrada.',
  CLIENTE_NO_ENCONTRADO: 'Ese cliente ya no existe.',
  CLIENTE_SIN_DOCUMENTO:
    'Ese cliente no tiene documento registrado. Complétalo en su ficha o factura a consumidor final.',
  VENTA_SIN_VALOR: 'Esa venta no tiene nada que facturar: su total es cero.',
  SUBTOTAL_NO_CUADRA:
    'Los renglones de la venta no suman su subtotal, así que no se puede facturar. Avísanos para revisarla.',
  IVA_NO_CUADRA:
    'El IVA cobrado en esa venta no corresponde a ninguna tarifa. Avísanos para revisarla.',
  TIENE_FACTURA_ELECTRONICA:
    'Esa venta ya tiene factura electrónica aceptada: no se anula, se corrige con una nota crédito.',
  FACTURA_SIN_CONFIRMAR:
    'La factura de esa venta se está enviando o falta confirmarla con la DIAN. Compruébala en Facturación antes de anular.',
  FACTURA_NO_EXISTE: 'Esa factura ya no existe.',
  FACTURA_NO_ACEPTADA: 'Solo se puede corregir una factura que la DIAN ya aceptó.',
  FACTURA_SIN_CUFE:
    'A esa factura le falta el código de la DIAN. Compruébala en Facturación antes de corregirla.',
  DOCUMENTO_NO_EXISTE: 'Ese documento ya no existe.',
  CONCEPTO_INVALIDO: 'Elige el motivo de la nota crédito.',
  RENGLON_INVALIDO: 'Uno de los renglones elegidos no es de esa factura.',
  NOTA_VACIA: 'La nota crédito no tiene nada: elige al menos un renglón.',
  CUFE_DUPLICADO: 'Ese código de la DIAN ya está registrado en otra factura.',
  ESTADO_INVALIDO: 'Ese estado no es válido.',
  DATOS_INVALIDOS: 'Algún dato del formulario no es válido. Revísalo.',
  TIPO_PERSONA_INVALIDO: 'Elige si es persona natural o jurídica.',
  MUNICIPIO_DANE_INVALIDO: 'El código DANE del municipio son 5 números (Bucaramanga: 68001).',
  DEPARTAMENTO_DANE_INVALIDO: 'El código DANE del departamento son 2 números (Santander: 68).',
  MUNICIPIO_NO_ES_DEL_DEPARTAMENTO:
    'Ese municipio no es de ese departamento: los dos primeros números del municipio son el departamento.',
  EMAIL_FISCAL_INVALIDO: 'Ese correo de facturación no es válido.',
  RESPONSABILIDADES_INVALIDAS:
    'Alguna responsabilidad fiscal no es válida. Se escriben como en el RUT: R-99-PN, O-47…',
  NIT_INVALIDO: 'Ese NIT no es válido: escribe solo los números.',
  TIPO_INVALIDO: 'Ese tipo de resolución no es válido.',
  FALTA_NUMERO_RESOLUCION: 'Escribe el número de la resolución de la DIAN.',
  RANGO_INVALIDO: 'El rango no es válido: el «hasta» tiene que ser mayor o igual que el «desde».',
  SIGUIENTE_FUERA_DE_RANGO: 'El próximo número tiene que estar dentro del rango autorizado.',
  ARCHIVO_NO_DISPONIBLE: 'Ese archivo todavía no está disponible. Inténtalo en un momento.',

  // Envío en curso: otra pestaña, otro equipo o el propio cobro tiene la
  // factura en camino. Reintentar ahora mandaría el MISMO número dos veces a
  // la DIAN; esperar un minuto no cuesta nada.
  DOCUMENTO_EN_ENVIO: 'Ya se está enviando, espera un momento. En menos de un minuto se sabe cómo quedó.',
  EMISION_EN_CURSO: 'Ya se está enviando, espera un momento. En menos de un minuto se sabe cómo quedó.',
  NOTA_EN_CURSO_OTRO_CONCEPTO:
    'Esa factura ya tiene una nota crédito en curso con otro motivo. Termínala primero en Facturación (reintentarla o comprobarla) y después emite la nueva.',
  // El caso que CommerceAdmin pagó: el número se usó en otra plataforma o el
  // contador se cargó por debajo. No se arregla reintentando.
  NUMERO_USADO_FUERA:
    'La DIAN ya tiene ese número, pero no salió de este sistema: se usó en otra plataforma. Netcloud tiene que revisarlo y renumerar el documento.',
  // La DIAN respondió y la base no pudo guardarlo: reintentar es justo lo
  // que no hay que hacer, porque puede estar aceptada.
  REGISTRO_FALLIDO:
    'La DIAN respondió, pero no se pudo guardar el resultado aquí. No la reintentes: compruébala en Facturación en unos minutos.',
  RESOLUCION_EN_USO:
    'Esa resolución ya tiene documentos: no se le puede cambiar el prefijo, el número ni el «desde». Si hay un error, crea una resolución nueva.',
  FACTURA_DE_PRUEBAS:
    'Esa factura es de pruebas (habilitación) y no existe para la DIAN de producción: no se corrige con nota crédito. Anula la venta normalmente.',
  // Ronda 5 (F1): la frontera es la PRIMERA vez que el salón entró a
  // producción y no se mueve al suspender y reactivar. Lo cobrado en
  // habilitación se quedó con su recibo; no es un fallo que haya que
  // reintentar, y decirlo evita que alguien la vuelva a cobrar para
  // «sacarle» la factura.
  YA_ESTUVO_EN_PRODUCCION:
    'Este salón ya facturó en producción y no puede volver a habilitación. Para parar la facturación, suspéndela o apágala.',
  CAMPO_DE_PLATAFORMA:
    'El plan, el estado y la licencia del salón los cambia Netcloud. Si necesitas un cambio, escríbenos.',
  VENTA_ANTERIOR_A_PRODUCCION:
    'Esa venta se cobró antes de que el salón empezara a facturar en producción, así que no lleva factura electrónica. Queda cobrada con su recibo; no hay nada que reintentar.',
  // Ronda 5 (F2): la base lo comprueba ANTES de anular la venta o escribir
  // nada, así que se puede decir que no se tocó.
  RESOLUCION_NC_NO_LISTA:
    'No hay una resolución de notas crédito de producción activa y vigente, así que la nota no se emitió y la venta sigue igual. Avísale a Netcloud para cargarla.',
  RESOLUCION_DE_PRUEBAS_EN_PRODUCCION:
    'El salón ya factura en producción: no se puede activar una resolución de pruebas. Carga la de producción, o desmarca «de pruebas» si esta es la real.',
  DOCUMENTO_NO_RENUMERABLE:
    'Ese documento no se puede renumerar: solo los que la DIAN nunca aceptó, que estén por emitir, rechazados o sin confirmar.',
  DOCUMENTO_CON_CUFE: 'Ese documento ya tiene código de la DIAN: la DIAN lo tiene y no se renumera.',
  MOTIVO_REQUERIDO: 'Escribe el motivo.',

  // ── Lo que responde el servidor de facturación ──────────────────────
  MATIAS_SIN_CONFIGURAR:
    'La conexión con el proveedor de facturación no está configurada en el servidor. Avísale a Netcloud; la venta quedó cobrada.',
  MATIAS_NO_RESPONDE:
    'El proveedor de facturación no respondió. La venta quedó cobrada; reintenta en unos minutos desde Facturación.',
  TOKEN_RECHAZADO:
    'El proveedor de facturación rechazó la conexión de este salón. Avísale a Netcloud para renovarla; la venta quedó cobrada.',
  AMBIGUO_EN_MATIAS:
    'El proveedor tiene más de un documento que podría ser este. Netcloud tiene que revisarlo antes de reintentar.',
  NADA_QUE_CONFIRMAR: 'Ese documento ya tiene veredicto: no hay nada que comprobar.',
  DOCUMENTO_NO_ACEPTADO: 'Solo se descarga o se envía un documento que la DIAN ya aceptó.',
  DOCUMENTO_DE_PRUEBAS: 'Es un documento de pruebas: no se envía por correo a nadie.',
  SIN_DESTINATARIO: 'Escribe al menos un correo para enviarla.',
  CORREO_INVALIDO: 'Algún correo no es válido. Revísalo.',
  CORREO_INACTIVO: 'El envío de correos no está configurado en el servidor. Avísale a Netcloud.',
  CORREO_NO_ENVIADO: 'No se pudo enviar el correo. Inténtalo de nuevo en un momento.',
  XML_NO_DISPONIBLE: 'El archivo de la DIAN todavía no está disponible. Inténtalo en unos minutos.',
  NO_SE_PUDO_FIRMAR: 'No se pudo preparar el enlace del archivo. Inténtalo en un momento.',
  TOTALES_NO_CUADRAN:
    'Los totales de la factura no cuadran con la venta, así que no se envió. Avísanos para revisarla.',
  FALTA_LA_VENTA: 'Falta la venta que se quiere facturar.',
  ACCION_INVALIDA: 'Esa acción no es válida.',
  CUERPO_INVALIDO: 'Algún dato no es válido. Recarga la página e inténtalo de nuevo.',
  SIN_SESION: 'Tu sesión venció. Vuelve a entrar.',
  SESION_INVALIDA: 'Tu sesión venció. Vuelve a entrar.',
};

/** Pesos a partir de centavos, sin depender de `utils` (que importa React). */
const pesos = (centavos: string) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
    .format(Number(centavos) / 100);

/** El nombre que ve la persona de cada módulo, para los mensajes. */
const NOMBRE_MODULO: Record<string, string> = {
  grooming: 'Peluquería',
  hotel: 'Hotel',
  daycare: 'Guardería',
  memberships: 'Membresías',
  portal: 'el Portal de clientes',
  reports: 'Reportes',
  facturacion_electronica: 'la Facturación electrónica',
  vetcare: 'VetCare',
};

/**
 * Códigos cuyo detalle —lo que va tras los dos puntos— sí le sirve a quien
 * lee: el DV correcto, cuánto queda por acreditar, qué falta para encender.
 * El resto de los detalles son identificadores y se callan.
 */
const DETALLADOS: Record<string, (detalle: string) => string> = {
  // `FORMULA_INCOMPLETA:<n>`: qué renglón de la fórmula falta completar.
  FORMULA_INCOMPLETA: (d) =>
    /^\d+$/.test(d)
      ? `Al medicamento ${d} de la fórmula le falta el nombre, la dosis, la frecuencia o la duración.`
      : 'A un medicamento de la fórmula le falta el nombre, la dosis, la frecuencia o la duración.',
  // La base dice QUÉ módulo falta: es lo que la persona necesita para pedirlo.
  MODULO_NO_CONTRATADO: (d) => {
    const nombre = NOMBRE_MODULO[d] ?? d;
    return nombre
      ? `Tu plan no incluye ${nombre}. Puedes agregarlo desde Mi suscripción.`
      : 'Tu plan no incluye ese módulo. Puedes agregarlo desde Mi suscripción.';
  },
  // La base dice QUIÉN ocupa la habitación o qué perro ya está hospedado:
  // es justo lo que hace falta para decidir qué cambiar.
  SUITE_OCUPADA: (d) =>
    d ? `La habitación ya la ocupa ${d}, de otro cliente, en esas fechas. Elige otra habitación u otras fechas.`
      : 'La habitación ya la ocupa otro cliente en esas fechas. Elige otra habitación u otras fechas.',
  MASCOTA_YA_HOSPEDADA: (d) =>
    d ? `${d} ya tiene una estadía en esas fechas. Revisa el calendario antes de reservar otra.`
      : 'Esa mascota ya tiene una estadía en esas fechas. Revisa el calendario.',
  NIT_DV_NO_COINCIDE: (d) =>
    /^\d$/.test(d)
      ? `El dígito de verificación no corresponde a ese NIT: debería ser ${d}.`
      : 'El dígito de verificación no corresponde a ese NIT.',
  NOTA_SUPERA_FACTURA: (d) =>
    /^\d+$/.test(d)
      ? `La nota crédito supera lo que queda de la factura: como mucho ${pesos(d)}.`
      : 'La nota crédito supera lo que queda de la factura.',
  // `NOTA_SUPERA_RENGLON:<orden>:<base disponible en centavos>`. La base
  // dice qué renglón y cuánto le queda; sin eso la cajera veía «no se pudo
  // completar» y volvía a marcar el mismo renglón, que ya estaba devuelto.
  NOTA_SUPERA_RENGLON: (d) => {
    const m = /^(\d+)\s*:\s*(\d+)$/.exec(d);
    if (!m) return 'Uno de los renglones elegidos ya se corrigió con otra nota crédito: no admite tanto.';
    const [, orden, base] = m;
    return Number(base) === 0
      ? `El renglón ${orden} ya se corrigió completo con otra nota crédito: no admite más.`
      : `El renglón ${orden} solo admite ${pesos(base!)} más: el resto ya se corrigió con otra nota crédito.`;
  },
  NUMERO_YA_USADO: (d) =>
    /^\d+$/.test(d)
      ? `Ya se emitió hasta el número ${d}: el contador no puede quedar por debajo.`
      : 'Ese número ya se usó: el contador no puede bajar.',
  // El detalle de FE_NO_LISTA lo escribe la base en español, uno por cada
  // cosa que falta, separados por « · ». Es justo la lista que hay que ver.
  FE_NO_LISTA: (d) =>
    d ? `Todavía no se puede encender: ${d}` : 'Todavía falta configurar algo para encenderla.',
};

/**
 * Un error que ya viene escrito para una persona.
 *
 * Las Edge Functions de facturación devuelven `{codigo, mensaje}` con el
 * mensaje redactado en español —«La DIAN rechazó la factura: …»—. Pasarlo
 * por los patrones de abajo lo estropearía: cualquier frase con «token»
 * acabaría diciendo «Tu sesión venció». Con esta clase se respeta tal cual.
 */
export class ErrorLegible extends Error {
  readonly codigo: string | null;
  constructor(mensaje: string, codigo: string | null = null) {
    super(mensaje);
    this.name = 'ErrorLegible';
    this.codigo = codigo;
  }
}

/**
 * El mensaje de un código que este archivo conoce, o `null` si no lo conoce.
 *
 * Lo usa quien recibe a la vez un código y un mensaje ya redactado —las
 * Edge Functions de facturación—: si el código es nuestro, manda nuestro
 * texto; si no, se respeta el que vino escrito.
 */
export function mensajeConocido(texto: string | null | undefined): string | null {
  const t = (texto ?? '').trim();
  if (!t) return null;
  const codigo = t.split(/[\s:]/)[0]?.toUpperCase();
  if (!codigo) return null;
  const detallado = DETALLADOS[codigo];
  if (detallado) return detallado(t.slice(codigo.length).replace(/^\s*:\s*/, '').trim());
  return MENSAJES[codigo] ?? null;
}

/** Lo que se dice cuando no se sabe qué pasó. */
const GENERICO = 'No se pudo completar la acción. Intenta de nuevo; si sigue igual, avísanos.';

/**
 * El mensaje para una persona, a partir de lo que devolvió el servidor.
 *
 * Los códigos llegan de varias formas —`APPOINTMENT_OVERLAP:abc123`,
 * `new row violates row-level security policy`, un Error de JavaScript— así
 * que se busca el código dentro del texto en vez de compararlo entero.
 */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ErrorLegible && error.message) return error.message;

  const texto: string =
    typeof error === 'string' ? error
    : ((error as { message?: string } | null)?.message ?? '');

  if (!texto) return GENERICO;

  // El código va al principio, con o sin detalle detrás de los dos puntos.
  const codigo = texto.trim().split(/[\s:]/)[0]?.toUpperCase();
  if (codigo && DETALLADOS[codigo]) {
    const detalle = texto.trim().slice(codigo.length).replace(/^\s*:\s*/, '').trim();
    return DETALLADOS[codigo](detalle);
  }
  if (codigo && MENSAJES[codigo]) return MENSAJES[codigo];

  // A veces viene envuelto en el mensaje de Postgres o de PostgREST.
  for (const [k, v] of Object.entries(MENSAJES)) {
    if (texto.includes(k)) return v;
  }

  // Casos de la infraestructura que tampoco debe ver el usuario en crudo.
  if (/row-level security|permission denied/i.test(texto))
    return MENSAJES.FORBIDDEN ?? GENERICO;
  if (/duplicate key|already exists/i.test(texto)) return 'Eso ya existe. Revisa si está repetido.';
  if (/foreign key|violates foreign key/i.test(texto))
    return 'No se puede porque hay otros datos que dependen de esto.';
  if (/not-null|null value in column/i.test(texto))
    return 'Falta un dato obligatorio. Revisa el formulario.';
  if (/JWT|jwt expired|token/i.test(texto))
    return 'Tu sesión venció. Vuelve a entrar.';
  if (/Failed to fetch|NetworkError|network/i.test(texto))
    return 'No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.';

  // El crudo va a la consola, nunca a la pantalla: sirve para diagnosticar y
  // al usuario no le dice nada. Pegarlo al mensaje —aunque fuera solo en
  // desarrollo— es arriesgarse a que se vea en producción el día que alguien
  // despliegue un build de dev.
  console.error('[error sin traducir]', texto);
  return GENERICO;
}
